import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';
import { guardarConConexion } from '../../../nucleo/api/cliente.js';
import { consultarMonedas } from '../../../nucleo/api/nucleoApi.js';
import { CampoDeImporte } from '../../../nucleo/importes/CampoDeImporte.jsx';
import { CintaDeVersionNueva } from '../../../nucleo/pwa/CintaDeVersionNueva.jsx';
import { consultarTiposDeCuenta, crearCuenta } from '../../cuentas/api/cuentasApi.js';
import { importe as formatearImporte } from '../formato.js';

const CLAVE_PRESTAMO = 'PRESTAMO';

/**
 * NUEVO PRÉSTAMO (P-20-01, RF-264, D-431, D-434).
 *
 * EL PRÉSTAMO Y SU PLAN SE GUARDAN EN DOS LLAMADAS, y es el patrón del 4/6 que el
 * contrato §1 fija: primero la cuenta con su deuda (esta pantalla) y después el plan
 * de pagos (la siguiente). Si la segunda no se completa, LA CUENTA QUEDA BIEN, con su
 * deuda, y la acción «Generar el plan de pagos» a la vista: nada queda a medias
 * (P-20-06, RN-778).
 *
 * LO QUE SE PIDE AQUÍ ES LO QUE TRAE SU ESTADO DE CUENTA (D-431, S-20-05): cuánto
 * debe HOY —el saldo pendiente, que viene impreso— y no el capital ya pagado, que
 * casi nunca viene y obligaría a sumar a mano los pedazos de capital de cada pago.
 * Omitirlo NO BLOQUEA: se toma el monto original con aviso y se corrige después con
 * la corrección de saldo inicial que ya existe.
 *
 * UN PRÉSTAMO NO TIENE NADA DE TARJETA (RN-752, D-434): no se le piden límite de
 * crédito, día de corte, día de pago, cuatro dígitos ni vigencia, y el servidor los
 * rechaza si alguien los manda. El tipo no se elige: esta pantalla ES la del tipo
 * Préstamo.
 *
 * AQUÍ NO SE CREA NINGÚN MOVIMIENTO: la deuda es la que ella declara (CA-20-01).
 */
export default function AltaDePrestamo() {
  const navegar = useNavigate();

  const [monedas, setMonedas] = useState([]);
  const [idTipoPrestamo, setIdTipoPrestamo] = useState(null);
  const [cargando, setCargando] = useState(true);

  const [nombre, setNombre] = useState('');
  const [idMoneda, setIdMoneda] = useState('');
  const [montoOriginal, setMontoOriginal] = useState('');
  const [deudaDeHoy, setDeudaDeHoy] = useState('');
  const [notas, setNotas] = useState('');

  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [respuestaDeMonedas, respuestaDeTipos] = await Promise.all([
          consultarMonedas(true),
          consultarTiposDeCuenta(true),
        ]);

        const activas = respuestaDeMonedas.monedas ?? respuestaDeMonedas;
        setMonedas(activas);
        if (activas.length === 1) setIdMoneda(String(activas[0].idMoneda));

        // El tipo se busca POR SU CLAVE ESTABLE, nunca por su posición ni por su
        // nombre visible: el nombre es presentación y la posición puede cambiar.
        const tipos = respuestaDeTipos.tipos ?? respuestaDeTipos;
        const prestamo = tipos.find((tipo) => (tipo.clave ?? tipo.claveSiembra) === CLAVE_PRESTAMO);
        setIdTipoPrestamo(prestamo ? prestamo.idTipoCuenta : null);
      } catch (fallo) {
        setError(fallo);
      } finally {
        setCargando(false);
      }
    })();
  }, []);

  const divisa = monedas.find((m) => String(m.idMoneda) === idMoneda)?.codigoISO ?? null;

  /*
   * D-431 · LA COTA SE AVISA, NO SE CORRIGE SOLA: deber más de lo que te prestaron no
   * es posible —el interés no sube la deuda, es gasto del mes—. LA VALIDACIÓN DE
   * VERDAD ES DEL SERVIDOR (RN-739d), con su literal; esto solo evita el viaje y que
   * el rechazo se descubra al guardar.
   */
  const debeMasDeLoPrestado = Number(deudaDeHoy) > 0
    && Number(montoOriginal) > 0
    && Number(deudaDeHoy) > Number(montoOriginal);

  const seOmitioLaDeuda = deudaDeHoy === '' && Number(montoOriginal) > 0;

  const datosCompletos = nombre.trim().length > 0
    && Boolean(idMoneda)
    && Boolean(idTipoPrestamo)
    && Number(montoOriginal) > 0
    && !debeMasDeLoPrestado;

  async function continuar() {
    if (!datosCompletos) return;
    setError(null);
    setGuardando(true);

    try {
      // LA PRIMERA DE LAS DOS LLAMADAS (contratos §1). El préstamo nace con su deuda
      // y sin un solo dato de tarjeta: los campos de tarjeta viajan NULOS porque en
      // un préstamo están PROHIBIDOS por el modelo (CK de tres ramas, D-434).
      const cuenta = await guardarConConexion(() => crearCuenta({
        nombre: nombre.trim(),
        idTipoCuenta: idTipoPrestamo,
        idMoneda: Number(idMoneda),
        // D-431 · omitido, se toma el monto original. El aviso ya está a la vista.
        saldoInicial: String(deudaDeHoy === '' ? montoOriginal : deudaDeHoy),
        cuatroUltimosDigitos: null,
        vigenciaMes: null,
        vigenciaAnio: null,
        limiteCredito: null,
        diaCorte: null,
        diaLimitePago: null,
        notas: notas.trim() || null,
        diferidoDeclarado: null,
      }));

      /*
       * Se REEMPLAZA el paso del formulario (RN-396): un toque atrás deja a la
       * persona donde estaba antes de capturar, no en el formulario ya guardado. El
       * monto original viaja a la pantalla del plan porque es un dato DEL CONTRATO,
       * no de la cuenta: la cuenta guarda lo que se debe hoy (D-431).
       */
      navegar(
        `/cuentas/prestamos/${cuenta.idCuenta}/plan`
        + `?montoOriginal=${encodeURIComponent(montoOriginal)}&recienCreado=1`,
        { replace: true },
      );
    } catch (fallo) {
      setError(fallo);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Shell titulo="Nuevo préstamo" volverA="/cuentas">
      <CintaDeVersionNueva />

      <div className="aviso i" role="status" data-prueba="prestamo-alta-para-que-sirve">
        <b className="tit">Para qué sirve esto</b>
        Aquí registras un préstamo del banco —personal, de nómina, de auto o una hipoteca— tal como va
        hoy, aunque lleve años pagándose. Primero el préstamo con lo que debes; enseguida su plan de
        pagos, con los que te faltan. Cada pago que registres se parte en dos: el capital baja lo que
        debes y el interés es gasto del mes.
      </div>

      <AvisoDeError error={error} />

      {!cargando && !idTipoPrestamo && (
        <div className="aviso e" role="status" data-prueba="prestamo-alta-sin-tipo">
          No se pudo cargar el tipo de cuenta «Préstamo». Vuelve a intentarlo en un momento.
        </div>
      )}

      <div className="card" style={{ margin: 12 }} data-prueba="prestamo-alta-datos">
        <div className="campo">
          <label htmlFor="nombre">NOMBRE DEL PRÉSTAMO</label>
          <input
            id="nombre"
            name="nombre"
            type="text"
            maxLength={60}
            value={nombre}
            onChange={(evento) => setNombre(evento.target.value)}
            data-prueba="prestamo-alta-campo-nombre"
          />
          <span className="ayuda">Como lo reconozcas tú: así va a aparecer en lo que vence.</span>
        </div>

        <div className="campo">
          <label htmlFor="idMoneda">MONEDA</label>
          <select
            id="idMoneda"
            name="idMoneda"
            value={idMoneda}
            onChange={(evento) => setIdMoneda(evento.target.value)}
            data-prueba="prestamo-alta-campo-moneda"
          >
            <option value="">Elige la moneda…</option>
            {monedas.map((moneda) => (
              <option key={moneda.idMoneda} value={moneda.idMoneda}>
                {moneda.codigoISO} · {moneda.nombre}
              </option>
            ))}
          </select>
          <span className="ayuda">
            La cuenta de la que salgan los pagos tiene que ser de esta misma moneda: no hay conversión.
          </span>
        </div>

        <div className="campo">
          <label htmlFor="montoOriginal">MONTO ORIGINAL</label>
          <CampoDeImporte
            id="montoOriginal"
            valor={montoOriginal}
            alCambiar={setMontoOriginal}
            codigoDivisa={divisa}
            contexto="el monto original del préstamo"
            data-prueba="prestamo-alta-campo-monto-original"
          />
          <span className="ayuda">Cuánto te prestaron al principio.</span>
        </div>

        <div className="campo">
          <label htmlFor="deudaDeHoy">¿CUÁNTO DEBES HOY?</label>
          <CampoDeImporte
            id="deudaDeHoy"
            valor={deudaDeHoy}
            alCambiar={setDeudaDeHoy}
            codigoDivisa={divisa}
            contexto="lo que debes hoy del préstamo"
            data-prueba="prestamo-alta-campo-deuda"
          />
          <span className="ayuda">
            El saldo pendiente que trae tu estado de cuenta. Si no lo tienes a la mano, déjalo vacío.
          </span>

          {/* D-431 · aviso que NO FRENA. */}
          {seOmitioLaDeuda && (
            <div className="aviso w" role="status" data-prueba="prestamo-alta-aviso-saldo-omitido">
              ⚠ Se va a tomar el monto original: {formatearImporte(montoOriginal, divisa)}. Puedes
              corregirlo después desde el préstamo.
            </div>
          )}

          {/* RN-739d · esto SÍ frena, con el literal del prototipo aprobado. */}
          {debeMasDeLoPrestado && (
            <div className="aviso e" role="status" data-prueba="prestamo-alta-error-deuda-mayor">
              No puedes deber más de lo que te prestaron ({formatearImporte(montoOriginal, divisa)}). El
              interés que pagas no sube tu deuda: es gasto del mes.
            </div>
          )}
        </div>

        <div className="campo">
          <label htmlFor="notas">NOTAS (OPCIONAL)</label>
          <input
            id="notas"
            name="notas"
            type="text"
            maxLength={500}
            value={notas}
            onChange={(evento) => setNotas(evento.target.value)}
          />
        </div>
      </div>

      <div className="card" style={{ margin: 12 }}>
        <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>QUÉ PASA AL CONTINUAR</h4>
        <span className="ayuda" data-prueba="prestamo-alta-que-pasa">
          Se guarda el préstamo con lo que debes y <b>no se registra ningún movimiento</b>. Enseguida
          capturas su plan de pagos: el pago mensual de tu contrato, el plazo, cuántos pagos ya
          hiciste y la fecha del próximo. Si algo se interrumpe ahí, el préstamo ya quedó bien
          guardado y podrás generar el plan cuando quieras.
        </span>

        <button
          className="btn ancho"
          type="button"
          disabled={guardando || cargando || !datosCompletos}
          onClick={continuar}
          data-prueba="prestamo-alta-continuar"
        >
          Guardar el préstamo y seguir con su plan
        </button>

        <button
          className="btn sec ancho"
          type="button"
          onClick={() => navegar('/cuentas', { replace: true })}
          data-prueba="prestamo-alta-cancelar"
        >
          Cancelar
        </button>
      </div>
    </Shell>
  );
}
